import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_EMPLOYEE, Employee } from 'src/utils/interfaces/interfaces';
import moment from 'moment';
import { Button } from '@heroui/react';
import { FaChevronRight } from 'src/icons';

export interface IMemberListProps {
  employee: Employee;
  onClose: Function;
  onAction: Function;
}

export function EmployeeList({
  employee = DEFAULT_EMPLOYEE,
  onAction = () => {},
  onClose = () => {},
}: IMemberListProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  const { t } = useTranslation();
  const employeeName =
    `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || 'N/A';
  const initials =
    `${employee?.firstName?.[0] ?? ''}${employee?.lastName?.[0] ?? ''}`.toUpperCase() ||
    'NA';

  return (
    <div
      className="grid grid-cols-12 gap-5 px-7 py-8 border-b"
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-light)',
      }}
      key={employee._id}
    >
      <div className="col-span-3 grid grid-cols-7 gap-5">
        <div
          className="inline col-span-2 shadow-xl rounded-full h-14 w-14 flex items-center justify-center text-sm font-semibold"
          style={{
            backgroundColor: 'var(--color-primary-light)',
            color: 'var(--color-primary)',
            border: '1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)',
          }}
        >
          {initials}
        </div>
        <div className="col-span-5">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {t('user.name')}
          </p>
          <div>
            <p className="text-sm" style={{ color: 'var(--text-main)' }}>
              {employeeName}
            </p>
          </div>
        </div>
      </div>
      <div className="col-span-2">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('user.contact_number')}
        </p>
        <div>
          <p className="text-sm" style={{ color: 'var(--text-main)' }}>
            {employee.phone || 'N/A'}
          </p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('employee.id_no')}
        </p>
        <div>
          <p className="text-sm capitalize" style={{ color: 'var(--text-main)' }}>
            {employee.idNumber || 'N/A'}
          </p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('employee.id_expiry')}
        </p>
        <div className="transition duration-150 ease-in-out">
          <p className="text-sm" style={{ color: 'var(--text-main)' }}>
            {employee?.idExpiry ? moment(employee.idExpiry).format('Do MMM YYYY') : 'N/A'}
          </p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('orders_page.created_at')}
        </p>
        <div className="transition duration-150 ease-in-out">
          <p className="text-sm" style={{ color: 'var(--text-main)' }}>
            {employee?.createdAt ? moment(employee.createdAt).format('Do MMM YYYY') : 'N/A'}
          </p>
        </div>
      </div>
      <div className="col-span-1 flex items-center justify-end">
        <Button
          variant="light"
          size="sm"
          onPress={() => onAction(employee)}
          onMouseDown={(e) => e.preventDefault()}
          isIconOnly
          className="rounded-full shadow-lg border transition-colors duration-200 outline-none focus:outline-none focus:ring-0 data-[focus-visible=true]:outline-none data-[focus-visible=true]:ring-0"
          style={{
            backgroundColor: 'var(--bg-surface-2)',
            borderColor: 'var(--border-light)',
            color: 'var(--color-primary)',
            outline: 'none',
            boxShadow: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
          startContent={<FaChevronRight />}
        />
      </div>
    </div>
  );
}
